import './style.css'
import { useState, useEffect, useContext, useCallback } from 'react';
import Loader from "../Loader";
import GlobalSVG from '../../utils/GlobalSVG';
import { HighchartsReact } from 'highcharts-react-official';
import Highcharts from "highcharts/highstock";
import GraphFuncs from '../../utils/GraphFuncs';
import { AppContext } from '../../utils/ContextProvider';
import API from '../../utils/API';
import Swal from 'sweetalert2';
import UnitToggle from '../UnitToggle';
import GlobalFuncs from '../../utils/GlobalFuncs';
import moment from 'moment';
import SlideToggle from '../SlideToggle';

const COLORS = ["#a3d237", "#4fa78b", "#006184", "#6e2b68", "#ffcf00", "#ff7500", "#db0661", "#007b4e", "#588C9E", "#007b4e"];
const UNIT_DICT = { kwh: "kWh", kva: "kVA", price: "$", generation: "kWh", co: "t" };
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function ReportGraph2(props) {
    const context = useContext(AppContext);

    const [stacked, setStacked] = useState("0");
    const [options, setOptions] = useState();
    const [showTotal, setShowTotal] = useState(true)
    const [currentData, setCurrentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showLabels, setShowLabels] = useState(true);
    const [percent, setPercent] = useState(false);
    const [open, setOpen] = useState(true);
    const [g2Visible, setG2Visible] = useState({ co: false, powerfactor2: false });
    const [unit, setUnit] = useState('price');

    const [finalCircuit, setFinalCircuit] = useState([]);
    const [finalCircuitCompare, setFinalCircuitCompare] = useState([]);
    const [categories, setCategories] = useState(null);
    const [subCategories, setSubCategories] = useState(null);
    const [distributions, setDistributions] = useState(null);
    const [baseCircuits, setBaseCircuits] = useState(null);

    const getMonthRanges = (startDate, endDate) => {
        const start = moment(startDate);
        const end = moment(endDate);
        const monthRanges = [];

        while (start.isSameOrBefore(end, 'day')) {
            const monthStart = moment([start.year(), start.month()]);
            const monthEnd = monthStart.clone().endOf('month');
            const rangeStart = start.isBefore(monthStart) ? start : monthStart;
            const rangeEnd = end.isBefore(monthEnd) ? end : monthEnd;

            monthRanges.push([rangeStart.format('YYYY-MM-DD'), rangeEnd.format('YYYY-MM-DD')]);
            start.add(1, 'month').startOf('month');
        }
        return monthRanges;
    };

    const formatDate = (inputDate) => {
        if (!inputDate) return inputDate;
        const [year, month] = inputDate.split('-');
        return `${MONTH_NAMES[parseInt(month, 10) - 1]} ${year}`;
    };

    const calculateCategoryPrices = (cats, kwhData, totalKwh, peakData, totalPeak, co, priceTotal) => {
        const tempCo = {};
        const tempPrice = {};

        cats.forEach(cat => {
            const catKwh = kwhData[cat] || 0;
            const catPeak = peakData[cat] || 0;

            tempCo[cat] = (catKwh / co.kwh_consumption) * co.co_consumption;

            const kwh_consumption_dollar = (catKwh / totalKwh) * priceTotal.kwh_consumption_dollar;
            const carbon_charge_dollar = (catKwh / totalKwh) * priceTotal.carbon_charge_dollar;
            const kva_consumption_dollar = (catPeak / totalPeak) * priceTotal.kva_consumption_dollar;

            const ratioOn = (kwh_consumption_dollar + carbon_charge_dollar + kva_consumption_dollar) /
                (priceTotal.kwh_consumption_dollar + priceTotal.carbon_charge_dollar + priceTotal.kva_consumption_dollar);

            const municipal_tax_dollar = ratioOn * priceTotal.municipal_tax_dollar;
            const pst_dollar = ratioOn * priceTotal.pst_dollar;
            const gst_dollar = ratioOn * priceTotal.gst_dollar;

            tempPrice[cat] = kwh_consumption_dollar + carbon_charge_dollar + kva_consumption_dollar +
                municipal_tax_dollar + pst_dollar + gst_dollar;
        });

        return { tempCo, tempPrice };
    };

    // Fetch base data
    useEffect(() => {
        if (!context.getBuildingInfo || !props.startEnd || props.compare) return;

        const ranges = getMonthRanges(props.startEnd[0], props.startEnd[1]);
        const mainCircuit = GraphFuncs.findCirctuitInHierarchy('Main Distribution', context.getBuildingInfo.hierarchy);
        const circuits = context.getBuildingInfo.e_channels.filter(item =>
            !['Source', 'Building Distribution', 'Capacitor Bank', null].includes(item)
        );

        const tempCats = Array.from(new Set(circuits.map(e => e.category)));
        const tempSubCats = Array.from(new Set(circuits.map(e => e.sub_category)));
        const tempDist = Array.from(new Set(circuits.map(e => e.building_distribution)));

        setCategories(tempCats);
        setSubCategories(tempSubCats);
        setDistributions(tempDist);

        const cancelToken = API.cancelToken();

        const getPeakBatchAsync = async () => {
            const monthsDict = {};
            ranges.forEach(e => { monthsDict[e[0]] = { range: e }; });

            for (let month in monthsDict) {
                try {
                    const [start, end] = monthsDict[month].range;

                    // Get generation
                    monthsDict[month].generation = 0;
                    const sources = [...structuredClone(context.getBuildingInfo.e_sources), mainCircuit];

                    for (let circuit of sources) {
                        if (!circuit || (circuit.category !== 'Building Distribution' && circuit.sub_category === 'Utility')) continue;
                        const res = await API.getElectricity(cancelToken, start, end, circuit.id, 60, 'kw');
                        monthsDict[month].generation += Object.values(res.data.series).reduce((sum, value) => sum + value, 0);
                    }

                    const co = (await API.getPriceCoElectric(cancelToken, start, end, mainCircuit.id, 60)).data;
                    const kwh = (await API.getCatConsumption(cancelToken, start, end, context.getBuildingInfo.building_id)).data;
                    const totalKwh = kwh.total;
                    const kwhCat = kwh.categories;
                    delete kwhCat["Capacitor Bank"];

                    monthsDict[month].kwh = {
                        category: kwhCat,
                        subCategory: kwh.subcategories,
                        distribution: kwh.distribution
                    };

                    const peak = await GraphFuncs.getPeakKva(cancelToken, mainCircuit, start, end);

                    if (Object.keys(peak)[0] !== 'null') {
                        const peakBatch = (await API.getPeakBatch(cancelToken, context.getBuildingInfo.building_id, Object.keys(peak)[0])).data;
                        const pfDict = (await API.getPeakBatch(cancelToken, context.getBuildingInfo.building_id, Object.keys(peak)[0], 'pf')).data;
                        const priceTotal = (await API.getPriceE(cancelToken, start, end, mainCircuit.id)).data;
                        const channelInfo = context.getBuildingInfo.e_channels;

                        const tempMain = channelInfo.find(e => e.category === 'Building Distribution');
                        const tempPf = tempMain ? pfDict[tempMain.channel_name] : 0;

                        const tempPeak = { category: {}, subCategory: {}, distribution: {} };

                        for (let circuit in peakBatch) {
                            const channelInfoFiltered = channelInfo.filter(e => e.channel_name.trim() === circuit);
                            if (channelInfoFiltered.length === 0) continue;

                            const { category, sub_category, building_distribution } = channelInfoFiltered[0];

                            tempPeak.category[category] = (tempPeak.category[category] || 0) + peakBatch[circuit];
                            tempPeak.subCategory[sub_category] = (tempPeak.subCategory[sub_category] || 0) + peakBatch[circuit];
                            tempPeak.distribution[building_distribution] = (tempPeak.distribution[building_distribution] || 0) + peakBatch[circuit];
                        }

                        monthsDict[month].pf = parseFloat(tempPf.toFixed(2));
                        monthsDict[month].kva = tempPeak;

                        const catPrices = calculateCategoryPrices(tempCats, kwhCat, totalKwh, tempPeak.category, Object.values(peak)[0], co, priceTotal);
                        const subCatPrices = calculateCategoryPrices(tempSubCats, kwh.subcategories, totalKwh, tempPeak.subCategory, Object.values(peak)[0], co, priceTotal);
                        const distPrices = calculateCategoryPrices(tempDist, kwh.distribution, totalKwh, tempPeak.distribution, Object.values(peak)[0], co, priceTotal);

                        monthsDict[month].co = {
                            category: catPrices.tempCo,
                            subCategory: subCatPrices.tempCo,
                            distribution: distPrices.tempCo
                        };
                        monthsDict[month].price = {
                            category: catPrices.tempPrice,
                            subCategory: subCatPrices.tempPrice,
                            distribution: distPrices.tempPrice
                        };
                        monthsDict[month].peak = Object.keys(peak)[0];
                    }
                } catch (e) {
                    console.log(e);
                    if (e.message === 'cancelling') return;
                    Swal.fire({
                        title: 'Error.',
                        text: 'Please try again later.',
                        icon: 'warning',
                        confirmButtonText: 'Ok',
                        confirmButtonColor: '#007b4e',
                    });
                }
            }
            setBaseCircuits(monthsDict);
            setFinalCircuit(monthsDict);
        };

        getPeakBatchAsync();
        return () => API.cancel(cancelToken);
    }, [context.getBuildingInfo, props.startEnd, props.compare]);

    // Fetch comparison data
    useEffect(() => {
        if (!props.compareStartEnd || !baseCircuits) return;

        if (!props.compare) {
            setFinalCircuit(baseCircuits);
            return;
        }

        const ranges = getMonthRanges(props.compareStartEnd[0], props.compareStartEnd[1]);
        const mainCircuit = GraphFuncs.findCirctuitInHierarchy('Main Distribution', context.getBuildingInfo.hierarchy);
        const cancelToken = API.cancelToken();

        const getPeakBatchAsync = async () => {
            const monthsDict = {};
            ranges.forEach(e => { monthsDict[e[0]] = { range: e }; });

            for (let month in monthsDict) {
                try {
                    const [start, end] = monthsDict[month].range;

                    monthsDict[month].generation = 0;
                    const sources = [...structuredClone(context.getBuildingInfo.e_sources), mainCircuit];

                    for (let circuit of sources) {
                        if (!circuit || (circuit.category !== 'Building Distribution' && circuit.sub_category === 'Utility')) continue;
                        const res = await API.getElectricity(cancelToken, start, end, circuit.id, 60, 'kw');
                        monthsDict[month].generation += Object.values(res.data.series).reduce((sum, value) => sum + value, 0);
                    }

                    const co = (await API.getPriceCoElectric(cancelToken, start, end, mainCircuit.id, 60)).data;
                    const kwh = (await API.getCatConsumption(cancelToken, start, end, context.getBuildingInfo.building_id)).data;
                    const totalKwh = kwh.total;
                    const kwhCat = kwh.categories;
                    delete kwhCat["Capacitor Bank"];

                    monthsDict[month].kwh = {
                        category: kwhCat,
                        subCategory: kwh.subcategories,
                        distribution: kwh.distribution
                    };

                    const peak = await GraphFuncs.getPeakKva(cancelToken, mainCircuit, start, end);

                    if (Object.keys(peak)[0] !== 'null') {
                        const peakBatch = (await API.getPeakBatch(cancelToken, context.getBuildingInfo.building_id, Object.keys(peak)[0])).data;
                        const pfDict = (await API.getPeakBatch(cancelToken, context.getBuildingInfo.building_id, Object.keys(peak)[0], 'pf')).data;
                        const priceTotal = (await API.getPriceE(cancelToken, start, end, mainCircuit.id)).data;
                        const channelInfo = context.getBuildingInfo.e_channels;

                        const tempMain = channelInfo.find(e => e.category === 'Building Distribution');
                        const tempPf = tempMain ? pfDict[tempMain.channel_name] : 0;

                        const tempPeak = { category: {}, subCategory: {}, distribution: {} };

                        for (let circuit in peakBatch) {
                            const channelInfoFiltered = channelInfo.filter(e => e.channel_name.trim() === circuit);
                            if (channelInfoFiltered.length === 0) continue;

                            const { category, sub_category, building_distribution } = channelInfoFiltered[0];

                            tempPeak.category[category] = (tempPeak.category[category] || 0) + peakBatch[circuit];
                            tempPeak.subCategory[sub_category] = (tempPeak.subCategory[sub_category] || 0) + peakBatch[circuit];
                            tempPeak.distribution[building_distribution] = (tempPeak.distribution[building_distribution] || 0) + peakBatch[circuit];
                        }

                        monthsDict[month].pf = parseFloat(tempPf.toFixed(2));
                        monthsDict[month].kva = tempPeak;

                        const catPrices = calculateCategoryPrices(categories, kwhCat, totalKwh, tempPeak.category, Object.values(peak)[0], co, priceTotal);
                        const subCatPrices = calculateCategoryPrices(subCategories, kwh.subcategories, totalKwh, tempPeak.subCategory, Object.values(peak)[0], co, priceTotal);
                        const distPrices = calculateCategoryPrices(distributions, kwh.distribution, totalKwh, tempPeak.distribution, Object.values(peak)[0], co, priceTotal);

                        monthsDict[month].co = {
                            category: catPrices.tempCo,
                            subCategory: subCatPrices.tempCo,
                            distribution: distPrices.tempCo
                        };
                        monthsDict[month].price = {
                            category: catPrices.tempPrice,
                            subCategory: subCatPrices.tempPrice,
                            distribution: distPrices.tempPrice
                        };
                        monthsDict[month].peak = Object.keys(peak)[0];
                    }
                } catch (e) {
                    console.log(e);
                    if (e.message === 'cancelling') return;
                    Swal.fire({
                        title: 'Error.',
                        text: 'Please try again later.',
                        icon: 'warning',
                        confirmButtonText: 'Ok',
                        confirmButtonColor: '#007b4e',
                    });
                }
            }
            setFinalCircuitCompare(monthsDict);
        };

        getPeakBatchAsync();
        return () => API.cancel(cancelToken);
    }, [baseCircuits, props.compareStartEnd, props.compare, categories, subCategories, distributions, context.getBuildingInfo]);

    // Generate chart series
    useEffect(() => {
        if (!finalCircuit || finalCircuit.length === 0 ||
            (props.compare && (!finalCircuitCompare || finalCircuitCompare.length === 0)) ||
            !categories) return;

        setLoading(true);
        const series = [];
        let index = 0;

        const getDataLabels = (includeTotal = true) => ({
            enabled: showLabels,
            formatter: function () {
                const isTop = this.point.stackY === this.point.stackTotal;
                if (isTop) {
                    if (g2Visible.powerfactor2 && unit === 'kva') {
                        return `<span style="text-align: center;">PF: ${this.point.options.pf}</span></br><span style="display: block; text-align: center;">${percent ? (Highcharts.numberFormat((this.y / this.point.options.total) * 100, 0) + "%") : (Highcharts.numberFormat(this.y, 0) + (this.point.options.percentCompare ? (' (' + (this.point.options.percentCompare >= 0 ? '+' : '') + Highcharts.numberFormat(this.point.options.percentCompare, 0) + '%)') : ''))}</span>`;
                    }
                    if (includeTotal && this.point.options.total !== undefined) {
                        return `<span style="text-align: center;">Total: ${Highcharts.numberFormat(this.point.options.total, 0)}</span></br><span style="display: block; text-align: center;">${percent ? (Highcharts.numberFormat((this.y / this.point.options.total) * 100, 0) + "%") : (Highcharts.numberFormat(this.y, 0) + (this.point.options.percentCompare ? (' (' + (this.point.options.percentCompare >= 0 ? '+' : '') + Highcharts.numberFormat(this.point.options.percentCompare, 0) + '%)') : ''))}</span>`;
                    }
                    return percent ? (Highcharts.numberFormat((this.y / (this.point.options.total ? this.point.options.total : this.point.options.y)) * 100, 0) + "%") : (Highcharts.numberFormat(this.y, 0) + (this.point.options.percentCompare ? (' (' + (this.point.options.percentCompare >= 0 ? '+' : '') + Highcharts.numberFormat(this.point.options.percentCompare, 0) + '%)') : ''))
                }
                return percent ? (Highcharts.numberFormat((this.y / (this.point.options.total ? this.point.options.total : this.point.options.y)) * 100, 0) + "%") : (Highcharts.numberFormat(this.y, 0) + (this.point.options.percentCompare ? (' (' + (this.point.options.percentCompare >= 0 ? '+' : '') + Highcharts.numberFormat(this.point.options.percentCompare, 0) + '%)') : ''))
            },
            verticalAlign: 'top',
            crop: false,
            overflow: 'allow',
            style: { fontSize: stacked === "0" ? '12px' : '11px', color: 'black', textOutline: 'none' },
            useHTML: true
        });

        const buildSeriesData = (categoryList, dataType) => {
            for (let cat of categoryList) {
                const catSeries = [];
                let monthIndex = 0;

                for (let month in finalCircuit) {
                    const monthData = finalCircuit[month];
                    let yValue = 0;
                    let total = 0;
                    let name = formatDate(month);

                    if (unit === 'generation') {
                        yValue = parseInt(monthData.generation);
                        name = formatDate(month);
                    } else if (unit === 'kva') {
                        name = GlobalFuncs.get15MinRange(monthData.peak) + " - " + monthData.peak.split(" ")[1];
                        yValue = monthData.kva && cat in monthData.kva[dataType] ? parseInt(monthData.kva[dataType][cat]) : 0;
                        total = monthData.kva ? Object.values(monthData.kva[dataType]).filter(e => !isNaN(e)).reduce((acc, val) => acc + parseInt(val), 0) : 0;
                    } else {
                        const unitData = monthData[unit];
                        yValue = unitData && cat in unitData[dataType] ? parseInt(unitData[dataType][cat]) : 0;
                        total = unitData ? Object.values(unitData[dataType]).filter(e => !isNaN(e)).reduce((acc, val) => acc + parseInt(val), 0) : 0;
                    }

                    const dataPoint = {
                        name,
                        y: yValue,
                        generation: parseInt(monthData.generation),
                        ...(unit === 'kva' && { pf: monthData.pf }),
                        ...(total && { total })
                    };

                    catSeries.push(dataPoint);

                    if (props.compare && finalCircuitCompare[Object.keys(finalCircuitCompare)[monthIndex]]) {
                        const compareMonth = Object.values(finalCircuitCompare)[monthIndex];
                        let yValueCompare = 0;
                        let totalCompare = 0;
                        let nameCompare = formatDate(Object.keys(finalCircuitCompare)[monthIndex]) + "(C)";

                        if (unit === 'generation') {
                            yValueCompare = parseInt(compareMonth.generation);
                        } else if (unit === 'kva') {
                            nameCompare = GlobalFuncs.get15MinRange(compareMonth.peak) + " - " + compareMonth.peak.split(" ")[1] + "(C)";
                            yValueCompare = compareMonth.kva && cat in compareMonth.kva[dataType] ? parseInt(compareMonth.kva[dataType][cat]) : 0;
                            totalCompare = compareMonth.kva ? Object.values(compareMonth.kva[dataType]).filter(e => !isNaN(e)).reduce((acc, val) => acc + parseInt(val), 0) : 0;
                        } else {
                            const unitData = compareMonth[unit];
                            yValueCompare = unitData && cat in unitData[dataType] ? parseInt(unitData[dataType][cat]) : 0;
                            totalCompare = unitData ? Object.values(unitData[dataType]).filter(e => !isNaN(e)).reduce((acc, val) => acc + parseInt(val), 0) : 0;
                        }

                        if (total && totalCompare) {
                            dataPoint.totalPercent = ((total - totalCompare) / totalCompare) * 100;
                        }
                        if (yValue && yValueCompare) {
                            dataPoint.percentCompare = ((yValue - yValueCompare) / yValueCompare) * 100;
                        }

                        catSeries.push({
                            name: nameCompare,
                            y: yValueCompare,
                            generation: parseInt(compareMonth.generation),
                            ...(unit === 'kva' && { pf: compareMonth.pf }),
                            ...(totalCompare && { total: totalCompare }),
                            ...(total && totalCompare && { totalPercent: ((totalCompare - total) / total) * 100 }),
                            ...(yValue && { percentCompare: ((yValueCompare - yValue) / yValue) * 100 }),
                        });
                        catSeries.push({ name: Object.keys(finalCircuitCompare)[monthIndex] + '!@#$DFWEG%T', y: null });
                    }
                    monthIndex++;
                }

                if (catSeries.some(e => e.y)) {
                    series.push({
                        name: cat,
                        data: catSeries,
                        color: COLORS[index],
                        dataLabels: getDataLabels(stacked !== "0" && showTotal)
                    });
                    index++;
                }

                if (unit === 'generation') break;
            }
        };

        if (stacked === "0") {
            const catSeries = [];
            let monthIndex = 0;

            for (let month in finalCircuit) {
                let total = 0;
                const monthData = finalCircuit[month];
                let dataPoint
                if (unit === 'generation') {
                    catSeries.push({ name: formatDate(month), y: parseInt(monthData.generation) });
                } else {
                    for (let cat of categories) {
                        const unitData = monthData[unit];
                        if (unitData && cat in unitData.category && unitData.category[cat]) {
                            total += parseFloat(unitData.category[cat]);
                        }
                    }

                    dataPoint = {
                        name: unit === 'kva' ? GlobalFuncs.get15MinRange(monthData.peak) + " - " + monthData.peak.split(" ")[1] : formatDate(month),
                        y: parseInt(total),
                        ...(unit === 'kva' && { pf: monthData.pf })
                    };
                    catSeries.push(dataPoint);
                }

                if (props.compare && finalCircuitCompare[Object.keys(finalCircuitCompare)[monthIndex]]) {
                    const compareMonth = Object.values(finalCircuitCompare)[monthIndex];
                    let totalCompare = 0;

                    if (unit === 'generation') {
                        totalCompare = parseInt(compareMonth.generation);
                    } else {
                        for (let cat of categories) {
                            const unitData = compareMonth[unit];
                            if (unitData && cat in unitData.category && unitData.category[cat]) {
                                totalCompare += parseFloat(unitData.category[cat]);
                            }
                        }
                    }

                    if (total && totalCompare) {
                        dataPoint.percentCompare = ((total - totalCompare) / totalCompare) * 100;
                    }

                    catSeries.push({
                        name: unit === 'kva'
                            ? GlobalFuncs.get15MinRange(compareMonth.peak) + " - " + compareMonth.peak.split(" ")[1] + "(C)"
                            : formatDate(Object.keys(finalCircuitCompare)[monthIndex]) + "(C)",
                        y: unit === 'generation' ? totalCompare : parseInt(totalCompare),
                        percentCompare: (unit === 'generation'
                            ? ((totalCompare - parseInt(monthData.generation)) / parseInt(monthData.generation))
                            : ((totalCompare - total) / total)) * 100,
                        ...(unit === 'kva' && { pf: compareMonth.pf })
                    });
                    catSeries.push({ name: Object.keys(finalCircuitCompare)[monthIndex] + '!@#$DFWEG%T', y: null });
                }
                monthIndex++;
            }

            series.push({
                name: "Total",
                data: catSeries,
                color: COLORS[0],
                dataLabels: getDataLabels(false)
            });
        } else if (stacked === "1") {
            buildSeriesData(categories, 'category');
        } else if (stacked === "2") {
            buildSeriesData(subCategories, 'subCategory');
        } else if (stacked === "3") {
            buildSeriesData(distributions, 'distribution');
        }

        setCurrentData(series);
    }, [finalCircuit, unit, categories, stacked, finalCircuitCompare, percent, g2Visible, showLabels, subCategories, distributions, props.compare, showTotal]);

    // Update chart options
    useEffect(() => {
        if (!currentData || currentData.length === 0) return;

        setOptions({
            chart: { type: 'column', zoomType: "x" },
            credits: { enabled: false },
            legend: {
                enabled: true,
                align: 'left',
                layout: 'horizontal',
                verticalAlign: 'bottom',
                itemMarginBottom: 0,
                itemMarginTop: 0,
            },
            title: { text: '', align: 'left' },
            subtitle: { text: '', align: 'left' },
            xAxis: {
                type: 'category',
                min: 0,
                max: !props.compare
                    ? (currentData[0].data.length < 12 ? currentData[0].data.length - 1 : 11)
                    : (currentData[0].data.length < 36 ? currentData[0].data.length - 1 : 35),
                accessibility: { description: 'Circuits' },
                scrollbar: { enabled: true },
                events: {
                    setExtremes: function (e) {
                        if (e.max >= this.dataMax || !e.max) return false;
                        if (e.min < 1 && e.max < 1) return false;
                    },
                    afterSetExtremes: function (e) {
                        if (e.min < 0 && e.max < 5) this.setExtremes(0, 5);
                        else if (e.min < 0) this.setExtremes(0, e.max);
                    }
                },
                labels: {
                    enabled: true,
                    formatter: function (e) {
                        return `${e.value}`.includes("!@#$DFWEG%T") ? "" : e.value;
                    },
                },
            },
            yAxis: {
                title: { align: 'high', offset: 0, text: '', rotation: 0, y: 10 },
                labels: { formatter: function () { return this.value; } }
            },
            plotOptions: {
                column: {
                    stacking: 'normal',
                    dataLabels: {
                        enabled: showLabels,
                        verticalAlign: 'top',
                        y: -7,
                        allowOverlap: true,
                        overflow: 'allow',
                    }
                },
            },
            series: currentData,
            exporting: {
                enabled: true,
                buttons: {
                    contextButton: {
                        menuItems: ['viewFullscreen', 'separator', 'downloadCSV', 'downloadPNG', 'downloadPDF']
                    },
                },
            }
        });

        setLoading(false);
        props.setComplete(e => e + "2");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentData, showLabels, props.setComplete, props.compare]);

    const handleReact = useCallback((chart) => {
        if (!chart?.container?.chart) return;
        chart = chart.container.chart;

        Highcharts.addEvent(chart, 'exportData', function (e) {
            if (unit === 'kva' && !e.dataRows[0].includes('PF')) {
                e.dataRows[0].push('PF');
                currentData[0].data.forEach((item, index) => {
                    if (e.dataRows[index + 1]) e.dataRows[index + 1].push(item.pf);
                });
            }
        });
    }, [currentData, unit]);

    return (
        <div>
            <div className="card tablet:p-8 rounded-lg relative mt-8">
                <h2 className="capitalize w-full laptop:text-3xl tablet:text-xl font-bold text-[#332D41] md:decoration-dashed laptop:p-0 tablet:p-8 mobile:p-8">
                    Monthly Consumption
                </h2>

                {props.startEnd && (
                    <p className='mb-4 font-light text-sm'>
                        Date Range: <span className='font-normal'>
                            {moment(props.startEnd[0]).format("MMM DD, YYYY")} - {moment(props.startEnd[1]).format("MMM DD, YYYY")}
                            ({GraphFuncs.getDifferenceInDays(props.startEnd[0], props.startEnd[1])} days)
                        </span>
                    </p>
                )}

                {props.compare && props.compareStartEnd?.length === 2 && (
                    <p className='mb-4 font-light text-sm'>
                        Comparative Range: <span className='font-normal'>
                            {moment(props.compareStartEnd[0]).format("MMM DD, YYYY")} - {moment(props.compareStartEnd[1]).format("MMM DD, YYYY")}
                            ({GraphFuncs.getDifferenceInDays(props.compareStartEnd[0], props.compareStartEnd[1])} days)
                        </span>
                    </p>
                )}

                <div className="flex flex-nowrap tablet:justify-end laptop:justify-start mobile:justify-end items-center rounded-lg min-h-[400px]">
                    <div className={`${open ? 'laptop:w-3/12 tablet:w-1/2 mobile:w-full' : 'w-0'} side-card tablet:p-5 mobile:p-4 flex-col z-10 laptop:static tablet:absolute mobile:absolute tablet:left-0 tablet:top-0 mobile:left-0 mobile:top-0 laptop:min-h-[400px] laptop:h-auto tablet:h-full mobile:h-full backround-white tablet:[card] transition-width duration-300 ease-in-out`}>
                        {open ? (
                            <div className='flex flex-col justify-between items-start laptop:min-h-[400px] laptop:h-auto tablet:h-full mobile:h-full mr-3'>
                                <div className='flex flex-col justify-start'>
                                    {(unit === 'kwh' || unit === 'co') && (
                                        <>
                                            <h2 className='mb-2 font-medium mt-4'>Building Total</h2>
                                            {unit === 'kwh' && (
                                                <p>{parseInt(props.compare && props.realPeakCompare ? props.virtualTotalCompare : props.virtualTotal).toLocaleString()} kWh</p>
                                            )}
                                            {unit === 'co' && (
                                                <p>{parseInt(props.compare && props.realPeakCompare ? props.totalCoCompare - props.totalCo : props.totalCo).toLocaleString()} t</p>
                                            )}
                                        </>
                                    )}

                                    {unit === 'kva' && props.realPeak && (
                                        <>
                                            <h2 className='mb-2 font-medium mt-4'>Building Peak</h2>
                                            <p>{parseInt(props.compare && props.realPeakCompare ? Object.values(props.realPeak)[0] - Object.values(props.realPeakCompare)[0] : Object.values(props.realPeak)[0]).toLocaleString()} kVA</p>
                                            <h2 className='mb-2 font-medium mt-4'>Reached At</h2>
                                            <p className='text-sm'>{GlobalFuncs.get15MinRange(Object.keys(props.realPeak)[0])} - {Object.keys(props.realPeak)[0].split(" ")[1]}</p>
                                            {props.compare && props.realPeakCompare && (
                                                <>
                                                    <h2 className='mb-2 font-medium mt-4'>And</h2>
                                                    <p className='text-sm'>{GlobalFuncs.get15MinRange(Object.keys(props.realPeakCompare)[0])} - {Object.keys(props.realPeakCompare)[0].split(" ")[1]}</p>
                                                </>
                                            )}
                                        </>
                                    )}

                                    <div className="grid grid-col justify-self-center">
                                        <h2 className='mb-2 font-medium mt-4'>Type</h2>
                                        <select className='w-1/2 my-2 border border-1 rounded border-[#C1C1C1] text-[#007b4e] w-[220px]' value={unit} onChange={(e) => setUnit(e.currentTarget.value)}>
                                            <option value="price">Utility Costs</option>
                                            <option value="generation">Source</option>
                                            <option value="kva">Electrical Demand</option>
                                            <option value="kwh">Electrical Consumption</option>
                                            <option value="co">Emissions</option>
                                        </select>
                                    </div>

                                    <select className='w-1/2 border border-1 rounded border-[#C1C1C1] text-[#007b4e] w-[220px]' value={stacked} onChange={(e) => setStacked(e.target.value)}>
                                        <option value="0">Totals</option>
                                        <option value="1">Categories</option>
                                        <option value="2">Sub-Categories</option>
                                        <option value="3">Building Distribution</option>
                                    </select>

                                    <UnitToggle setOptions={setPercent} options={percent} option1={true} option2={false} button1="Percentage" button2="Amount" />
                                    <UnitToggle setOptions={setShowLabels} options={showLabels} option1={true} option2={false} button1="Labels On" button2="Labels Off" />
                                   { stacked !== "0" && showLabels && <label label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={showTotal}
                                            onChange={e => setShowTotal(e.target.checked)}
                                            className='mr-2'
                                        />
                                        Show total
                                    </label>}
                                    {unit === 'kva' && <SlideToggle setOptions={setG2Visible} options={g2Visible} id={"powerfactor2"} index={1} name={"Power Factor"} />}
                                </div>

                                <div className='mt-4 flex flex-nowrap justify-start items-center cursor-pointer' onClick={() => setOpen(false)}>
                                    <div className='border border-light-green rounded-full w-8 h-8 flex flex-nowrap items-center justify-center'>
                                        {GlobalSVG.leftArrow()}
                                    </div>
                                    <p className='ml-3 cursor-pointer'>Hide options</p>
                                </div>
                            </div>
                        ) : (
                            <div className='flex flex-col justify-end h-full items-center'>
                                <div onClick={() => setOpen(true)} className='cursor-pointer border border-light-green rounded-full tablet:w-8 tablet:h-8 mobile:w-6 mobile:h-6 flex flex-nowrap items-center justify-center'>
                                    {GlobalSVG.rightArrow()}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={`col-span-2 ${open ? 'w-9/12' : 'laptop:w-full mobile:w-11/12'}`}>
                        {UNIT_DICT[unit]}
                        {options && <HighchartsReact highcharts={Highcharts} options={options} ref={handleReact} />}
                        {loading && <Loader />}
                    </div>
                </div>
            </div>
        </div>
    );
}